<template>
  <div class="scroll-container" ref="scrollContainer">
    <div class="marquee-wrapper">
      <div class="marquee-container" ref="container">
        <canvas ref="canvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UserMarqueeMobile",
  data() {
    return {
      users: [],
      defaultAvatar: require("@/assets/images/logo.png"),
      canvas: null,
      ctx: null,
      // 移动端尺寸
      width: 375, // 移动端宽度
      height: 150, // 移动端高度（根据头像和文字调整）
      position: 0,
      animationId: null,
      lastTime: null,
      isPaused: false,
      imageCache: {},
      imageLoadCount: 0,
      allImagesLoaded: false,
      virtualItems: [],
      virtualPosition: 0,
      pendingUpdate: false,
      itemWidth: 120, // 移动端item宽度，每个头像区域120px
      // 移动端优化参数
      mobileSpeed: 30, // 移动端滚动速度（像素/秒），比PC端慢
      visibleItems: 3, // 移动端可见项目数
      avatarSize: 57, // 头像大小
      avatarRadius: 28.5, // 头像半径
      // 布局参数
      padding: 10,
      avatarTop: 10,
      nameTop: 80, // 名字位置
      remarkTop: 100, // 备注位置
      fontSize: {
        name: 14, // 名字字体大小
        remark: 12, // 备注字体大小
      },
    };
  },
  computed: {
    formattedUsers() {
      return this.users.map((user) => ({
        avatar: user.img || this.defaultAvatar,
        name: user.name,
        remark: user.remark,
      }));
    },
    singleLoopWidth() {
      return this.itemWidth * this.formattedUsers.length;
    },
  },
  watch: {
    users: {
      handler(newVal) {
        console.log("用户数据:", newVal);
        if (newVal && newVal.length > 0) {
          if (this.allImagesLoaded) {
            this.updateItems(newVal);
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.$http
      .get("/emcee-api/website/wsite_home")
      .then((res) => {
        this.users = res.data.emceeList;
        this.preloadAllImages();
      })
      .catch((err) => {
        console.error("获取用户数据失败:", err);
      });
  },
  mounted() {
    this.initCanvas();
    window.addEventListener("resize", this.handleResize);
    // 添加触摸事件支持
    this.$refs.scrollContainer?.addEventListener(
      "touchstart",
      this.handleTouchStart
    );
    this.$refs.scrollContainer?.addEventListener(
      "touchmove",
      this.handleTouchMove
    );
    this.$refs.scrollContainer?.addEventListener(
      "touchend",
      this.handleTouchEnd
    );
  },
  beforeDestroy() {
    this.stopAnimation();
    window.removeEventListener("resize", this.handleResize);
    // 移除触摸事件
    this.$refs.scrollContainer?.removeEventListener(
      "touchstart",
      this.handleTouchStart
    );
    this.$refs.scrollContainer?.removeEventListener(
      "touchmove",
      this.handleTouchMove
    );
    this.$refs.scrollContainer?.removeEventListener(
      "touchend",
      this.handleTouchEnd
    );

    Object.keys(this.imageCache).forEach((key) => {
      this.imageCache[key].src = "";
    });
  },
  methods: {
    initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext("2d");

      // 处理高DPI屏幕
      const dpr = window.devicePixelRatio || 1;
      this.canvas.width = this.width * dpr;
      this.canvas.height = this.height * dpr;
      this.ctx.scale(dpr, dpr);

      this.canvas.style.width = "100%";
      this.canvas.style.height = `${this.height}px`;
    },

    preloadAllImages() {
      this.imageLoadCount = 0;
      this.formattedUsers.forEach((item) => {
        if (!this.imageCache[item.avatar]) {
          const img = new Image();
          img.src = item.avatar;
          img.onload = () => {
            this.imageCache[item.avatar] = img;
            this.imageLoadCount++;
            if (this.imageLoadCount === this.formattedUsers.length) {
              this.allImagesLoaded = true;
              this.startAnimation();
            }
          };
          img.onerror = () => {
            // 加载失败时使用默认头像
            console.warn(`头像加载失败: ${item.avatar}`);
            const defaultImg = new Image();
            defaultImg.src = this.defaultAvatar;
            defaultImg.onload = () => {
              this.imageCache[item.avatar] = defaultImg;
              this.imageLoadCount++;
              if (this.imageLoadCount === this.formattedUsers.length) {
                this.allImagesLoaded = true;
                this.startAnimation();
              }
            };
          };
        } else {
          this.imageLoadCount++;
          if (this.imageLoadCount === this.formattedUsers.length) {
            this.allImagesLoaded = true;
            this.startAnimation();
          }
        }
      });
    },

    updateItems(newItems) {
      const formatted = newItems.map((user) => ({
        avatar: user.img || this.defaultAvatar,
        name: user.name,
        remark: user.remark,
      }));

      formatted.forEach((item) => {
        if (!this.imageCache[item.avatar]) {
          const img = new Image();
          img.src = item.avatar;
          img.onload = () => {
            this.imageCache[item.avatar] = img;
          };
        }
      });

      const currentPos = this.position % this.singleLoopWidth;
      this.virtualPosition = currentPos > this.width ? 0 : this.singleLoopWidth;
      this.virtualItems = [...formatted, ...formatted];
      this.pendingUpdate = true;
    },

    drawFrame() {
      if (!this.allImagesLoaded || !this.ctx) return;

      // 清除画布
      this.ctx.clearRect(0, 0, this.width, this.height);

      if (this.pendingUpdate) {
        const distanceToEdge = Math.min(
          Math.abs(this.position - 0),
          Math.abs(this.position - this.singleLoopWidth)
        );

        if (distanceToEdge > this.width) {
          this.formattedUsers = this.virtualItems;
          this.position = this.virtualPosition;
          this.pendingUpdate = false;
        }
      }

      // 计算当前可见区域
      const visibleStart = Math.floor(this.position / this.itemWidth);

      // 绘制两组数据以实现无限滚动
      for (let i = 0; i < 2; i++) {
        const groupOffset = i * this.formattedUsers.length;
        const startIndex =
          (visibleStart + groupOffset) % this.formattedUsers.length;

        for (let j = 0; j < this.formattedUsers.length; j++) {
          const itemIndex = (startIndex + j) % this.formattedUsers.length;
          const item = this.formattedUsers[itemIndex];
          const x =
            i * this.singleLoopWidth +
            j * this.itemWidth -
            (this.position % this.itemWidth);

          // 只绘制可见区域内的项目
          if (x + this.itemWidth > 0 && x < this.width) {
            this.drawItem(item, x);
          }
        }
      }
    },

    drawItem(item, x) {
      const centerX = x + this.itemWidth / 2;

      // 绘制圆形头像
      if (this.imageCache[item.avatar]?.complete) {
        this.ctx.save();
        this.ctx.beginPath();
        // 绘制圆形裁剪区域
        this.ctx.arc(
          centerX,
          this.avatarTop + this.avatarRadius,
          this.avatarRadius,
          0,
          Math.PI * 2
        );
        this.ctx.clip();

        // 绘制头像
        this.ctx.drawImage(
          this.imageCache[item.avatar],
          centerX - this.avatarRadius,
          this.avatarTop,
          this.avatarSize,
          this.avatarSize
        );

        this.ctx.restore();

        // 绘制圆形边框
        this.ctx.beginPath();
        this.ctx.arc(
          centerX,
          this.avatarTop + this.avatarRadius,
          this.avatarRadius,
          0,
          Math.PI * 2
        );
        this.ctx.strokeStyle = "#f0f0f0";
        this.ctx.lineWidth = 1;
        this.ctx.stroke();
      }

      // 绘制名字
      this.ctx.fillStyle = "#333333";
      this.ctx.font = `bold ${this.fontSize.name}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif`;
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";

      const truncatedName = this.truncateText(item.name, this.itemWidth - 20);
      this.ctx.fillText(truncatedName, centerX, this.nameTop);

      // 绘制备注
      this.ctx.fillStyle = "#666666";
      this.ctx.font = `${this.fontSize.remark}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif`;

      const truncatedRemark = this.truncateText(
        item.remark,
        this.itemWidth - 20
      );
      this.ctx.fillText(truncatedRemark, centerX, this.remarkTop);
    },

    truncateText(text, maxWidth) {
      if (!text) return "";

      // 如果文本已经很短，直接返回
      const metrics = this.ctx.measureText(text);
      if (metrics.width <= maxWidth) return text;

      let truncated = text;
      while (
        this.ctx.measureText(truncated + "...").width > maxWidth &&
        truncated.length > 0
      ) {
        truncated = truncated.substring(0, truncated.length - 1);
      }
      return truncated + "...";
    },

    startAnimation() {
      if (this.animationId) return;

      const animate = (timestamp) => {
        if (!this.lastTime) this.lastTime = timestamp;
        const deltaTime = timestamp - this.lastTime;
        this.lastTime = timestamp;

        if (!this.isPaused) {
          // 使用移动端速度
          this.position += (this.mobileSpeed * deltaTime) / 1000;

          if (this.position >= this.singleLoopWidth) {
            this.position -= this.singleLoopWidth;
            if (this.pendingUpdate) {
              this.formattedUsers = this.virtualItems;
              this.position = this.virtualPosition;
              this.pendingUpdate = false;
            }
          }
        }

        this.drawFrame();
        this.animationId = requestAnimationFrame(animate);
      };

      this.animationId = requestAnimationFrame(animate);
    },

    stopAnimation() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
    },

    handleResize() {
      // 移动端固定宽度，无需响应式
    },

    // 触摸事件处理
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.wasPaused = this.isPaused;
      this.isPaused = true;
    },

    handleTouchMove(e) {
      if (!this.touchStartX) return;

      const touchX = e.touches[0].clientX;
      const deltaX = touchX - this.touchStartX;

      // 根据拖动调整位置
      this.position -= deltaX * 0.5;
      this.touchStartX = touchX;

      // 限制位置范围
      if (this.position < 0) this.position = 0;
      if (this.position > this.singleLoopWidth)
        this.position = this.singleLoopWidth;

      this.drawFrame();
    },

    handleTouchEnd() {
      this.touchStartX = null;
      this.isPaused = this.wasPaused || false;
    },

    pause() {
      this.isPaused = true;
    },

    resume() {
      this.isPaused = false;
      this.lastTime = null;
    },
  },
};
</script>

<style lang="less" scoped>
.scroll-container {
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 10px 0;
  touch-action: pan-x; /* 允许水平触摸滚动 */

  .marquee-wrapper {
    height: 150px;
    position: relative;

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      width: 30px;
      z-index: 2;
      pointer-events: none;
    }

    &::before {
      left: 0;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0.9),
        rgba(255, 255, 255, 0)
      );
    }

    &::after {
      right: 0;
      background: linear-gradient(
        to left,
        rgba(255, 255, 255, 0.9),
        rgba(255, 255, 255, 0)
      );
    }

    .marquee-container {
      background: transparent;
      width: 100%;
      height: 100%;
      position: relative;

      canvas {
        display: block;
        width: 100%;
        height: 100%;
      }
    }
  }
}

/* 移动端适配 */
@media (max-width: 375px) {
  .scroll-container {
    width: 100%;
  }
}

/* 加载状态样式 */
.loading-placeholder {
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 8px;

  .loading-text {
    color: #999;
    font-size: 14px;
  }
}
</style>
